import { StoreIllustrated } from '@jbhi-fi/lanyard-ui/components/Icons';
import React, { ReactNode, useState } from 'react';
import { styles } from '../styles/Availability.css';
import Spinner from '@jbhi-fi/lanyard-ui/components/Spinner/Spinner';
import { DeliveryLocation, FulfilmentStore, StoreLocation } from '../../localisation/utils/types';
import { StoreAvailabilityDetails } from '../utils/types';
import StoreAvailabilityResults from '../store-availability-results/StoreAvailabilityResults';
import LocationSearch from '../../location-search/LocationSearch';
import ArrowIcon from '@jbhi-fi/lanyard-ui/components/Icons/Arrow';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import AvailabilityOptionStatus from '@jbhi-fi/lanyard-ui/components/AvailabilityOptionStatus';
import {
  CHECK_ALL_INSTORE_MESSAGE,
  DELIVERY_ONLY_MESSAGE,
  ERROR_MESSAGE_UNAVAILABLE_INSTORE,
  NOT_AVAILABILE_WITHIN_RANGE_MESSAGE,
  UNAVAILABLE_MESSAGE,
} from '../utils/constants';
import { DisplayModelOptionStatus } from '../../localisation/components/product-fulfilment-info/PickupInStoreContainer/DisplayModelOptionStatus/DisplayModelOptionStatus';
import { AvailabilityVariantType } from '../availability-wrapper/AvailabilityWrapper';
import { checkHideInStoreForFamily } from '../../localisation/utils/checkHideInStoreFamily';
import { useLocalisationContext } from '../../localisation/context/LocalisationContext';
import { useShopifyProduct } from '../../../../api/shopify/ShopifyProduct';
import { getAvailabilityText } from '../utils/availabilityUtils';

export type StoreAvailabilityProps = {
  selectedStore: StoreLocation & { closingTime?: ReactNode; isStoreOpenInNexthour?: boolean };
  isLoading?: boolean;
  inStoreOptions?: Array<StoreAvailabilityDetails>;
  yourStoreFulfilmentOptions?: FulfilmentStore;
  isPreOrder?: boolean;
  error?: string;
  variant?: AvailabilityVariantType;
  isDeliveryOnly?: boolean;
  isErrorMessage?: string;
  hasOutOfRangeStoreOptions?: boolean;
  handleStoreLocationUpdated: (location: DeliveryLocation) => void;
  onClose: () => void;
  updateMySelectedStoreFromId: (storeId: number, gtmString?: string) => void;
  onStoreClick?: (storeId: number) => void;
};

const StoreAvailability = ({
  selectedStore,
  isLoading,
  inStoreOptions,
  yourStoreFulfilmentOptions,
  isPreOrder,
  error,
  variant,
  isDeliveryOnly,
  isErrorMessage = '',
  hasOutOfRangeStoreOptions = true,
  handleStoreLocationUpdated,
  onClose,
  updateMySelectedStoreFromId,
  onStoreClick,
}: StoreAvailabilityProps) => {
  const [showMore, setShowMore] = useState(false);
  const resultsToDisplay = 5;

  const { excludedProductFamilyForInStore } = useLocalisationContext();
  const { productMetafields } = useShopifyProduct();

  const productFamily = window.Jbhifi['product.metafields']?.online_product?.value?.Family?.Title || '';
  const isHideInstoreOptionForFamily =
    checkHideInStoreForFamily(productFamily, excludedProductFamilyForInStore) &&
    !yourStoreFulfilmentOptions?.isInStoreAvailable;

  const testidPrefix = 'store-availability-';

  const getUnavailableMessaging = () => {
    const isDeliveryAvailableMetafield = productMetafields?.Availability?.CanBuyOnline;
    const isPickupAvailableNearby = inStoreOptions && inStoreOptions[0]?.options.some(option => option.isAvailable);
    if (
      !yourStoreFulfilmentOptions?.isOnlyDisplayAvailable &&
      (isPickupAvailableNearby || !hasOutOfRangeStoreOptions)
    ) {
      if (isPickupAvailableNearby) return UNAVAILABLE_MESSAGE;
      if (isDeliveryAvailableMetafield) return ERROR_MESSAGE_UNAVAILABLE_INSTORE;
      return 'Unavailable at your store';
    }
    if (hasOutOfRangeStoreOptions) return NOT_AVAILABILE_WITHIN_RANGE_MESSAGE;
    return UNAVAILABLE_MESSAGE;
  };

  const GetStoreAvailability = ({
    yourStoreFulfilmentOptions,
    isDeliveryOnly,
  }: {
    yourStoreFulfilmentOptions: FulfilmentStore | undefined;
    isDeliveryOnly: boolean | undefined;
  }) => {
    if (isErrorMessage) {
      return (
        <AvailabilityOptionStatus classes={styles.selectedStoreIsDelivery} name={isErrorMessage} isAvailable={false} />
      );
    }
    if (yourStoreFulfilmentOptions) {
      if (yourStoreFulfilmentOptions.isOnlyDisplayAvailable) {
        return <DisplayModelOptionStatus />;
      } else if (
        !yourStoreFulfilmentOptions.isCncAvailable &&
        !yourStoreFulfilmentOptions.isInStoreAvailable &&
        isDeliveryOnly
      ) {
        return (
          <AvailabilityOptionStatus
            classes={styles.selectedStoreIsDelivery}
            name={DELIVERY_ONLY_MESSAGE}
            isAvailable={false}
          />
        );
      } else if (
        !yourStoreFulfilmentOptions.isCncAvailable &&
        !yourStoreFulfilmentOptions.isInStoreAvailable &&
        !yourStoreFulfilmentOptions.isOnlyDisplayAvailable
      ) {
        return (
          <AvailabilityOptionStatus
            classes={styles.selectedStoreIsDelivery}
            name={error ? getUnavailableMessaging() : UNAVAILABLE_MESSAGE}
            isAvailable={false}
          />
        );
      } else {
        return (
          <>
            <div className={styles.availabilityOptionStatusContainer}>
              <AvailabilityOptionStatus
                classes={styles.optionStatus}
                name={getAvailabilityText(
                  'Click & Collect',
                  selectedStore.isStoreOpenInNexthour,
                  yourStoreFulfilmentOptions.isAwaitingStock,
                  yourStoreFulfilmentOptions.isCncAvailable
                )}
                isAvailable={yourStoreFulfilmentOptions.isCncAvailable}
              />
              {!isPreOrder && !isHideInstoreOptionForFamily ? (
                <AvailabilityOptionStatus
                  classes={styles.optionStatus}
                  name="In-store"
                  isAvailable={yourStoreFulfilmentOptions.isInStoreAvailable}
                />
              ) : null}
            </div>
            {yourStoreFulfilmentOptions.isCncAvailable &&
            yourStoreFulfilmentOptions.importantMessaging &&
            (isPreOrder || yourStoreFulfilmentOptions.isAwaitingStock) ? (
              <div className={styles.importantMessagingContainer}>
                <p className={styles.importantMessaging}>{yourStoreFulfilmentOptions.importantMessaging}</p>
              </div>
            ) : null}
          </>
        );
      }
    } else {
      return (
        <>
          <AvailabilityOptionStatus name="Click & Collect" isAvailable={false} classes={styles.optionStatus} />
          {!isPreOrder && (
            <AvailabilityOptionStatus name="In-store" isAvailable={false} classes={styles.optionStatus} />
          )}
        </>
      );
    }
  };

  const handleShowMore = () => {
    setShowMore(!showMore);
  };

  return (
    <div className={styles.availabilityContainer} data-testid={`${testidPrefix}container`}>
      <div className={styles.heading({ variant: variant })} data-testid={`${testidPrefix}header-title`}>
        Your store
      </div>
      <div className={styles.availabilityHeader({ variant: variant })} data-testid={`${testidPrefix}header`}>
        <div data-testid={`${testidPrefix}header-icon-wrapper`}>
          <StoreIllustrated width="50px" height="33.69px" />
        </div>

        <div data-testid={`${testidPrefix}header-container`}>
          <div className={styles.headerContent} data-testid={`${testidPrefix}header-content`}>
            <div
              data-testid={`${testidPrefix}header-store-name`}
              className={styles.selectedStoreName}
              onClick={() => onStoreClick?.(selectedStore?.id)}
            >
              {selectedStore?.name}
            </div>
            <div data-testid={`${testidPrefix}header-store-opening-hours`} className={styles.storeHours}>
              {selectedStore?.closingTime}
            </div>
          </div>

          <div className={styles.availabilityOptionsContainer} data-testid={`${testidPrefix}options-container`}>
            {!isLoading ? (
              <GetStoreAvailability
                yourStoreFulfilmentOptions={yourStoreFulfilmentOptions}
                isDeliveryOnly={isDeliveryOnly}
              />
            ) : null}
          </div>
        </div>
      </div>
      <div className={styles.searchLocationContainer} data-testid={`${testidPrefix}search-location-container`}>
        <div className={styles.searchLocationHeader} data-testid={`${testidPrefix}search-location-title`}>
          {CHECK_ALL_INSTORE_MESSAGE}
        </div>
        <LocationSearch
          id="store-availability-search"
          initialValue={selectedStore?.aggregatedAddress}
          showUseMyLocationButton={false}
          onLocationSelect={handleStoreLocationUpdated}
        />
      </div>

      {isLoading ? (
        <div className={styles.loader}>
          <Spinner />
        </div>
      ) : !isErrorMessage ? (
        error ? (
          <div className={styles.noResultsContainer} data-testid={`${testidPrefix}search-location-error`}>
            {error}
          </div>
        ) : (
          <>
            <StoreAvailabilityResults
              storeAvailabilityResults={
                inStoreOptions?.slice(0, showMore ? inStoreOptions.length : resultsToDisplay) || []
              }
              isPreOrder={isPreOrder}
              onClose={onClose}
              updateMySelectedStoreFromId={updateMySelectedStoreFromId}
              onStoreClick={onStoreClick}
              isHideInstoreFamily={checkHideInStoreForFamily(productFamily, excludedProductFamilyForInStore)}
            />
            {inStoreOptions && inStoreOptions.length > 5 ? (
              <div className={styles.viewMoreContainer}>
                <Button
                  iconPosition="left"
                  type="linkWithIcon"
                  icon={<ArrowIcon direction="down" />}
                  label={showMore ? 'Less stores' : 'More stores'}
                  onClick={handleShowMore}
                />
              </div>
            ) : null}
          </>
        )
      ) : null}
    </div>
  );
};

export default StoreAvailability;
